import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, Lock } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import i18n from '@/admin/lib/i18n'
import {
  headlessLogin,
  isAuthenticated,
  isMfaPending,
  isSessionConflict,
  authenticate2fa,
} from '@/shared/lib/auth-api'
import { useSession } from '@/admin/hooks/use-session'
import { Logo } from '@/admin/components/ui/logo'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'

// Dev-only quick-login - credentials match seed output.
const DEV_STAFF_ACCOUNT = { label: 'Staff Admin', email: 'staff@iwarranty.co', password: 'staff123!' }

type LoginValues = { email: string; password: string }
type MfaValues = { code: string }

export function StaffLoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const nextUrl = searchParams.get('next') || ''
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials')
  const { data: session, isLoading: sessionLoading } = useSession()

  // Already signed in - send the user back where they were headed (or the staff
  // dashboard) instead of stranding them on the login form. Skip during the
  // second-factor prompt so the pending-MFA token isn't read as a full login.
  useEffect(() => {
    if (sessionLoading || step === 'mfa') return
    if (!session?.is_authenticated) return
    navigate(nextUrl || '/staff/dashboard', { replace: true })
  }, [sessionLoading, session, nextUrl, step, navigate])

  const loginFields = useMemo<FormField[]>(() => [
    {
      type: 'email',
      name: 'email',
      label: t('common.email'),
      placeholder: t('staffLogin.emailPlaceholder'),
      value: '',
      autoFocus: true,
      autoComplete: 'email',
      validationSchema: z.string().min(1, 'Enter a valid email').email('Enter a valid email'),
    },
    {
      type: 'password',
      name: 'password',
      label: t('common.password'),
      placeholder: '••••••••',
      value: '',
      autoComplete: 'current-password',
      validationSchema: z.string().min(1, 'Password is required'),
    },
  ], [t])
  const mfaFields = useMemo<FormField[]>(() => [
    {
      type: 'text',
      name: 'code',
      label: t('staffLogin.codeLabel'),
      placeholder: t('staffLogin.codePlaceholder'),
      value: '',
      autoFocus: true,
      autoComplete: 'one-time-code',
      inputMode: 'numeric',
      maxLength: 6,
      validationSchema: z.string().length(6, 'Code must be 6 digits'),
    },
  ], [t])
  // Shared landing after either single-factor login or completing the second
  // factor.
  async function finishLogin() {
    await queryClient.invalidateQueries({ queryKey: ['session'] })
    void i18n.reloadResources()
    toast.success('Signed in')
    navigate(nextUrl || '/staff/dashboard')
  }

  const { Form: LoginForm, ref: loginFormRef } = useForm(
    {
      name: 'staff-login-form',
      submit: 'footer',
      fields: loginFields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as LoginValues
      setLoading(true)
      try {
        const result = await headlessLogin(values.email, values.password)
        if (isAuthenticated(result)) {
          await finishLogin()
        } else if (isMfaPending(result)) {
          // Credentials valid; the pending-stage session token is stored.
          setStep('mfa')
          toast.info('Enter your verification code')
        } else if (isSessionConflict(result)) {
          // headlessLogin already tried to heal a 409 transparently (tear down
          // the token + cookie session, retry). A 409 still here is a genuinely
          // unrecoverable conflict - say so honestly rather than the misleading
          // "invalid password" the generic branch would show on an empty body.
          toast.error(
            result.errors?.[0]?.message ??
              t('staffLogin.sessionConflict', {
                defaultValue:
                  'Another session is still active in this browser. Refresh the page and sign in again.',
              }),
          )
        } else {
          toast.error(result.errors?.[0]?.message ?? 'Invalid email or password')
        }
      } catch (err) {
        toastApiError(err, 'Invalid email or password');
      } finally {
        setLoading(false)
      }
    },
    [loginFields, nextUrl],
  )
  const { Form: MfaForm } = useForm(
    {
      name: 'staff-mfa-form',
      submit: 'footer',
      fields: mfaFields,
    },
    async (rawValues) => {
      const values = rawValues as unknown as MfaValues
      setLoading(true)
      try {
        const result = await authenticate2fa(values.code)
        if (isAuthenticated(result)) {
          await finishLogin()
        } else {
          toast.error(result.errors?.[0]?.message ?? 'Invalid verification code')
        }
      } catch (err) {
        toastApiError(err, 'Invalid verification code');
      } finally {
        setLoading(false)
      }
    },
    [mfaFields, nextUrl],
  )

  async function quickLogin(email: string, password: string) {
    const form = loginFormRef.current
    if (!form) return

    await form.setFieldValue('email', email, false)
    await form.setFieldValue('password', password, false)
    await form.submitForm()
  }

  // Hold the form back until the session probe settles, so an authenticated
  // visitor is redirected (above) rather than flashing the login form.
  if (step !== 'mfa' && (sessionLoading || session?.is_authenticated)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[var(--bg)]">
      {/* ── Left: Premium brand panel ────────────────────── */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between bg-gradient-to-br from-[#1c5a48] via-[#1e6b55] to-[#247a62] p-12 relative overflow-hidden">
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] bg-[length:24px_24px] opacity-[0.04]"
        />
        <div className="relative z-10">
          <div className="mb-20">
            <Logo size="lg" variant="white" />
          </div>

          <h1 className="font-display text-[42px] leading-[1.15] tracking-[-0.01em] text-[#faf9f7]">
            {t('staffLogin.tagline')}
          </h1>
          <p className="mt-3 max-w-sm text-base leading-relaxed text-[var(--bg)]">
            {t('staffLogin.description')}
          </p>
        </div>

        <div className="relative z-10 flex gap-5 text-[11px] font-normal uppercase tracking-[0.2em] text-[var(--ink-3)]">
          <span>SOC 2 Compliant</span>
          <span>&middot;</span>
          <span>Multi-Tenant</span>
          <span>&middot;</span>
          <span>Real-time Analytics</span>
        </div>
      </div>
      {/* ── Right: Login Form ────────────────────────── */}
      <div className="flex flex-1 items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-[360px]">
          {/* Mobile logo */}
          <div className="mb-10 lg:hidden">
            <Logo size="lg" />
          </div>

          {step === 'credentials' ? (
            <>
              <div className="mb-8">
                {/* Desktop logo */}
                <div className="hidden lg:block mb-6">
                  <Logo size="md" />
                </div>

                <h2 className="font-display text-[36px] leading-[1.1] text-[var(--ink)]">
                  {t('staffLogin.signIn')}
                </h2>
                <p className="clara-small mt-1">{t('staffLogin.signInDescription')}</p>
              </div>

              <LoginForm className="space-y-4" />

              <Button
                type="submit"
                form="staff-login-form"
                disabled={loading}
                className="mt-4 w-full py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] text-[var(--bg)] transition-all duration-150 disabled:opacity-50 cursor-pointer bg-[var(--green)] hover:bg-[var(--green-hover)]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t('login.signInButton')}
              </Button>

              {/* Dev quick-login */}
              {import.meta.env.DEV && (
                <div className="mt-6 border-t-[0.5px] border-t-[var(--rule)] pt-5">
                  <p className="mb-3 text-[10px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
                    Dev quick login
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void quickLogin(DEV_STAFF_ACCOUNT.email, DEV_STAFF_ACCOUNT.password)}
                    className="w-full rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2 text-left text-[11px] text-[var(--ink-3)] transition-colors duration-100 hover:text-[var(--ink)]"
                  >
                    {DEV_STAFF_ACCOUNT.label} - {DEV_STAFF_ACCOUNT.email}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-center mb-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-subtle)]">
                  <Lock className="h-5 w-5 text-[var(--ink-3)]" />
                </div>
              </div>
              <h2 className="mb-1 text-center font-display text-[28px] leading-[1.15] text-[var(--ink)]">
                {t('staffLogin.verification')}
              </h2>
              <p className="clara-small text-center mb-8">{t('staffLogin.mfaDescription')}</p>
              <MfaForm className="space-y-5" />
              <Button
                type="submit"
                form="staff-mfa-form"
                disabled={loading}
                className="mt-4 w-full py-2.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] text-[var(--bg)] transition-all duration-150 disabled:opacity-50 cursor-pointer bg-[var(--green)] hover:bg-[var(--green-hover)]"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                ) : (
                  t('staffLogin.verifyButton')
                )}
              </Button>
              <Button
                type="button"
                variant="link"
                onClick={() => setStep('credentials')}
                className="w-full cursor-pointer py-2 text-[11px] text-[var(--ink-3)]"
              >
                {t('staffLogin.backToLogin')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
